import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getRoomTypeByResortIdResorTypeId,
  uploadRoomTypeImage,
  deleteRoomTypeImage,
} from "../../../../../../../apis/property";
import { useParams } from "react-router-dom";
import {
  XIcon,
  HomeIcon,
  TagIcon,
  DocumentTextIcon,
  UsersIcon,
  CollectionIcon,
  PhotographIcon,
  UploadIcon,
  TrashIcon,
  RefreshIcon,
} from "@heroicons/react/outline";

const RoomTypeDetailModal = ({
  isOpen,
  onClose,
  roomTypeId,
  onImageUpload,
}) => {
  // State for image preview modal
  const [previewImage, setPreviewImage] = useState(null);
  const { id } = useParams();
  const [roomType, setRoomType] = useState(null);
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchRoomTypeDetails = async () => {
    setLoading(true);
    try {
      const response = await getRoomTypeByResortIdResorTypeId(id, roomTypeId);
      setRoomType(response.data);
    } catch (error) {
      console.log("Error fetching room type details:", error);
      toast.error("Failed to load room type details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && roomTypeId) {
      fetchRoomTypeDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, roomTypeId]);

  const handleImageChange = (e) => {
    setImage(e.target.files[0]);
  };

  const handleImageUpload = async () => {
    if (!image) {
      toast.error("Please select an image to upload.");
      return;
    }

    setUploading(true);
    try {
      const formDataImage = new FormData();
      formDataImage.append("image", image);
      formDataImage.append("room_type_id", roomTypeId);

      await uploadRoomTypeImage(id, formDataImage);
      toast.success("Image uploaded successfully");
      setImage(null);
      fetchRoomTypeDetails();
      if (onImageUpload) onImageUpload();
    } catch (error) {
      toast.error("Failed to upload image");
      console.error("Error uploading image:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (window.confirm("Are you sure you want to delete this image?")) {
      try {
        await deleteRoomTypeImage(id, imageId);
        toast.success("Image deleted successfully");
        fetchRoomTypeDetails();
        if (onImageUpload) onImageUpload();
      } catch (error) {
        toast.error("Failed to delete image");
        console.error("Error deleting image:", error);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        ></div>
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="relative w-full max-w-4xl transform transition-all">
            <div className="modal-container">
              <div className="modal-header">
                <div className="flex items-center space-x-3">
                  <div className="modal-icon">
                    <HomeIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="modal-title">Room Type Details</h3>
                    <p className="modal-subtitle">
                      View and manage room type information
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="modal-close-btn"
                  type="button"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>

              {loading ? (
                <div className="loading-container">
                  <RefreshIcon className="loading-spinner" />
                  <p className="loading-text">Loading room type details...</p>
                </div>
              ) : (
                <>
                  <div className="modal-body">
                    {/* Room Type Information */}
                    <div className="info-grid">
                      <div className="info-card">
                        <div className="info-label">
                          <TagIcon className="info-icon" />
                          <span>Code</span>
                        </div>
                        <div className="info-value">{roomType.code}</div>
                      </div>

                      <div className="info-card">
                        <div className="info-label">
                          <HomeIcon className="info-icon" />
                          <span>Name</span>
                        </div>
                        <div className="info-value">{roomType.name}</div>
                      </div>

                      <div className="info-card">
                        <div className="info-label">
                          <CollectionIcon className="info-icon" />
                          <span>Total Count</span>
                        </div>
                        <div className="info-value">{roomType.total_count}</div>
                      </div>

                      <div className="info-card">
                        <div className="info-label">
                          <UsersIcon className="info-icon" />
                          <span>Min Occupancy</span>
                        </div>
                        <div className="info-value">
                          {roomType.min_occupancy}
                        </div>
                      </div>

                      <div className="info-card">
                        <div className="info-label">
                          <UsersIcon className="info-icon" />
                          <span>Max Occupancy</span>
                        </div>
                        <div className="info-value">
                          {roomType.max_occupancy}
                        </div>
                      </div>

                      <div className="info-card">
                        <div className="info-label">
                          <CollectionIcon className="info-icon" />
                          <span>Category</span>
                        </div>
                        <div className="info-value">
                          {roomType.roomCategoryName || "N/A"}
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="description-section">
                      <div className="section-header">
                        <DocumentTextIcon className="section-icon" />
                        <h4 className="section-title">Description</h4>
                      </div>
                      <p className="description-text">{roomType.description}</p>
                    </div>

                    {/* Image Upload Section */}
                    <div className="upload-section">
                      <div className="section-header">
                        <PhotographIcon className="section-icon" />
                        <h4 className="section-title">Room Images</h4>
                      </div>
                      <div className="upload-area">
                        <div className="upload-input-group">
                          <label className="upload-label">
                            <UploadIcon className="upload-icon" />
                            <span>Choose Image</span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              className="hidden-input"
                            />
                          </label>
                          {image && (
                            <span className="file-name">{image.name}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleImageUpload}
                          disabled={!image || uploading}
                          className="upload-button"
                        >
                          {uploading ? (
                            <>
                              <svg
                                className="animate-spin h-5 w-5 mr-2"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                  fill="none"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                              Uploading...
                            </>
                          ) : (
                            <>
                              <UploadIcon className="w-5 h-5" />
                              Upload Image
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Image Gallery */}
                    {roomType.images && roomType.images.length > 0 ? (
                      <div className="image-gallery">
                        {roomType.images.map((image) => (
                          <div key={image.id} className="image-card">
                            <img
                              src={image.url}
                              alt={image.name}
                              className="gallery-image cursor-pointer"
                              onClick={() => setPreviewImage(image.url)}
                              title="Click to preview"
                            />
                            <button
                              onClick={() => handleDeleteImage(image.id)}
                              className="delete-image-btn"
                              title="Delete image"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-images">
                        <PhotographIcon className="no-images-icon" />
                        <p>No images uploaded yet</p>
                      </div>
                    )}
                    {/* Image Preview Modal */}
                    {previewImage && (
                      <div
                        className="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-80"
                        onClick={() => setPreviewImage(null)}
                      >
                        <div
                          className="relative max-w-3xl w-full flex flex-col items-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            className="absolute top-4 right-4 bg-white bg-opacity-80 rounded-full p-2 shadow hover:bg-opacity-100 transition"
                            onClick={() => setPreviewImage(null)}
                            type="button"
                            aria-label="Close preview"
                          >
                            <XIcon className="h-6 w-6 text-gray-700" />
                          </button>
                          <img
                            src={previewImage}
                            alt="Preview"
                            className="rounded-lg max-h-[80vh] object-contain shadow-lg"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="modal-footer">
                    <button
                      onClick={onClose}
                      type="button"
                      className="btn-close"
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .modal-container {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .modal-icon {
          width: 3rem;
          height: 3rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .modal-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
          margin: 0;
        }

        .modal-subtitle {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0.25rem 0 0 0;
        }

        .modal-close-btn {
          padding: 0.5rem;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 0.5rem;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }

        .loading-container {
          padding: 4rem 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .loading-spinner {
          width: 3rem;
          height: 3rem;
          color: #8b5cf6;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .loading-text {
          margin-top: 1rem;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .modal-body {
          padding: 2rem;
          max-height: 70vh;
          overflow-y: auto;
        }

        .modal-body::-webkit-scrollbar {
          width: 8px;
        }

        .modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .modal-body::-webkit-scrollbar-thumb {
          background: #8b5cf6;
          border-radius: 4px;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .info-card {
          background: linear-gradient(135deg, #f9fafb, #ffffff);
          padding: 1rem;
          border-radius: 0.75rem;
          border: 1px solid #e5e7eb;
        }

        .info-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.5rem;
        }

        .info-icon {
          width: 1rem;
          height: 1rem;
          color: #8b5cf6;
        }

        .info-value {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1f2937;
        }

        .description-section,
        .upload-section {
          margin-bottom: 2rem;
          padding: 1.5rem;
          background: #f9fafb;
          border-radius: 0.75rem;
          border: 1px solid #e5e7eb;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .section-icon {
          width: 1.25rem;
          height: 1.25rem;
          color: #8b5cf6;
        }

        .section-title {
          font-size: 1rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0;
        }

        .description-text {
          font-size: 0.875rem;
          color: #6b7280;
          line-height: 1.6;
          margin: 0;
        }

        .upload-area {
          display: flex;
          gap: 1rem;
          align-items: flex-start;
          flex-wrap: wrap;
        }

        .upload-input-group {
          flex: 1;
          min-width: 250px;
        }

        .upload-label {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          background: white;
          border: 2px dashed #d1d5db;
          border-radius: 0.75rem;
          color: #6b7280;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .upload-label:hover {
          border-color: #8b5cf6;
          color: #8b5cf6;
          background: #f3f4f6;
        }

        .upload-icon {
          width: 1.25rem;
          height: 1.25rem;
        }

        .hidden-input {
          display: none;
        }

        .file-name {
          display: block;
          margin-top: 0.5rem;
          font-size: 0.875rem;
          color: #6b7280;
          font-style: italic;
        }

        .upload-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          color: white;
          border: none;
          border-radius: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px -1px rgba(139, 92, 246, 0.3);
        }

        .upload-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.4);
        }

        .upload-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .image-gallery {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 1rem;
        }

        .image-card {
          position: relative;
          border-radius: 0.75rem;
          overflow: hidden;
          aspect-ratio: 1;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .image-card:hover {
          transform: scale(1.05);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
        }

        .gallery-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .delete-image-btn {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          width: 2rem;
          height: 2rem;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: all 0.2s ease;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);
        }

        .image-card:hover .delete-image-btn {
          opacity: 1;
        }

        .delete-image-btn:hover {
          background: #dc2626;
          transform: scale(1.1);
        }

        .no-images {
          text-align: center;
          padding: 3rem 1rem;
          color: #9ca3af;
        }

        .no-images-icon {
          width: 4rem;
          height: 4rem;
          margin: 0 auto 1rem;
          color: #d1d5db;
        }

        .modal-footer {
          padding: 1.5rem;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
          display: flex;
          justify-content: flex-end;
        }

        .btn-close {
          padding: 0.75rem 1.5rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-weight: 600;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-close:hover {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        @media (max-width: 768px) {
          .info-grid {
            grid-template-columns: 1fr;
          }

          .upload-area {
            flex-direction: column;
          }

          .upload-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </>
  );
};

export default RoomTypeDetailModal;
